import React from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { IReportManagementProps } from './report-management.models';
import { PagePath } from '../../navigation/navigation.enums';
import { FocusPanelLoaderLayer } from '../../components/general/loading-state/loader-layers/focus-panel-loader-layer/focus-panel-loader-layer';
import { FetchType } from '../../enums';
import { useAppActions, useAppState } from '../../overmind';
import { Anchor, Button, PanelHeader, Toggle, kebabCase, useMatchScreenWidth, FieldController, TextField, NameValidator, TextArea } from '@keplerco/core';
import reportManagementStyles from './report-management.module.css';
import classNames from 'classnames';

interface RequestModel {
  companySlug: string;
  reportType: string;
  body: { name: string; description: string };
}

export function ReportManagementPanel(props: IReportManagementProps): JSX.Element {
  const { report, isLoading, tempValue, handleToggle } = props;

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const isMobile = useMatchScreenWidth('mobile');

  const { control, handleSubmit } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur', defaultValues: props.report as FieldValues });

  return (
    <FocusPanelLoaderLayer path={PagePath.reportManagement}>
      <div className="panelContent panelForm">
        <PanelHeader supertitle="Report Management" title="Report details" subtitle="Edit report details" divider />

        <form
          id="editReport"
          className="panelForm"
          onSubmit={handleSubmit(async fieldValues => {
            actions.startLoader({ path: PagePath.reportManagement, type: FetchType.DialogFetching });

            const request: RequestModel = {
              companySlug: companyVariables.slug!,
              reportType: report!.reportType,
              body: { name: fieldValues.name, description: fieldValues.description },
            };
            await actions.editReportTemplate(request);
            props.onSubmit();

            actions.stopLoader(PagePath.reportManagement);
          })}
        >
          <div>
            {isMobile && (
              <div className={classNames('card', reportManagementStyles.toggleCard)}>
                <h3 style={{ whiteSpace: 'nowrap' }}>Report status</h3>

                <div className={reportManagementStyles.toggleCardDivider} />

                <div className={reportManagementStyles.toggle} onClick={event => event.stopPropagation()}>
                  OFF
                  <Toggle id={`${kebabCase(report.name)}-report`} name="Report" value={tempValue ?? report.isVisibleToCompany} isLoading={isLoading} clickableArea="toggle" toggleBackgroundColour="primary" toggleForegroundColour="white" size="large" onChange={handleToggle} />
                  ON
                </div>
              </div>
            )}

            <FieldController
              control={control}
              name="name"
              rules={new NameValidator('Enter report name')}
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    {...field}
                    type="text"
                    label="Report Name"
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter report name',
                    }}
                  />
                );
              }}
            />

            <FieldController
              control={control}
              name="description"
              render={({ field }) => {
                return <TextArea {...field} label="Description" responsive />;
              }}
            />
          </div>

          <footer className="panelFooter">
            <Anchor onClick={props.onCancel}>Cancel</Anchor>

            <Button type="button">Submit</Button>
          </footer>
        </form>
      </div>
    </FocusPanelLoaderLayer>
  );
}
